/**
 * 关于页 — 公司介绍（2026-09-19 二稿：全屏翻页式）。
 * 依据《时述智能（Chronuspeak Tech）— 品牌与公司体系》：每幕占满一屏，
 * CSS scroll-snap 吸附产生翻页感（原生滚动，键盘/触摸/滚轮行为不变）；
 * 幕内元素 IntersectionObserver 进入视口后依次渐显（stagger）；
 * 第一屏 slogan 居中 + 底部下箭头；右侧圆点导航可跳幕。
 * 幕序：Hero → 01 名称由来 → 02 使命愿景 → Slogan 幕间 → 03 价值观（手风琴）
 *       → 04 Builder 理念 → 06 品牌架构 → 收尾 CTA（核心引擎幕 2026-09-19 按用户指示删除）。
 * 样式 about.css（ab- 前缀仅本页）；token 全走 prototype.css :root，Light/Dark 自动适配。
 * 可访问性：手风琴真实 button + aria-expanded/controls；prefers-reduced-motion 下
 * 渐显/浮动箭头/平滑滚动全部停用（内容恒可见，吸附保留——吸附为落点修正非动画）；
 * 页头悬浮于滚动区之外（.ab-page 固定一屏高，内部 .ab-viewport 滚动）。
 * H1 仍取 GET /public/pages/about（后台可编辑）；正文 = 页面内固定双语内容。
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, Ref } from 'react';
import { Link } from 'react-router';
import type { PageContent } from '@chronospeak/contracts';
import type { Envelope } from '../lib/api-types';
import { http } from '../lib/http';
import { useLocale } from '../lib/locale';
import { DraftBadge, LocaleFallbackNotice } from '../components/bilingual';
import { SiteHeader } from '../components/SiteHeader';
import grainImg from '../assets/hero-grain.png';
import '../styles/about.css';

/** 双语文案对 */
interface Bi {
  zh: string;
  en: string;
}

/** 品牌架构树（决策 7 冻结层级：根 → Innaframe → 三产品端 + Originals；纯展示） */
const ARCH_TREE = [
  {
    name: 'Innaframe',
    zh: '统一 AI 创作引擎 · 正在抵达',
    en: 'Unified AI creation engine · coming into view',
  },
  {
    name: 'Innaframe Vibe Creative Town',
    zh: 'Web 创作平台 · 正在抵达',
    en: 'Web creation platform · coming into view',
  },
  {
    name: 'Innaframe Vibe Creative Lab',
    zh: '线下沉浸式创作空间 · 正在抵达',
    en: 'Offline immersive creation space · coming into view',
  },
  {
    name: 'Innaframe Originals',
    zh: '未来原创内容厂牌 · Coming later',
    en: 'Future original content label · Coming later',
  },
] as const;

/** 五项价值观（品牌体系文档 §二，解释为摘录） */
const VALUES: { zh: string; en: string; body: Bi }[] = [
  {
    zh: '自由',
    en: 'Freedom',
    body: {
      zh: '创作的本质是自由的。自由不是无边界，而是在清晰的框架内拥有最大的表达空间——每一位 Builder 选择自己的创作方向、叙事方式与表达语言。自由同时意味着责任：你的自由不能伤害他人的自由。',
      en: 'Creation is, at its heart, free. Freedom here is not boundlessness, but the widest room for expression within a clear frame — every Builder chooses their own direction, narrative and voice. Freedom also means responsibility: yours must not harm the freedom of others.',
    },
  },
  {
    zh: '开放',
    en: 'Openness',
    body: {
      zh: '开放是对世界的基本姿态。我们接纳不同类型、不同背景、不同风格的创作人，也接纳新技术、新工具、新流程。信息不被囤积，知识被共享而非私有化。',
      en: 'Openness is our basic posture toward the world. We welcome creators of every type, background and style, and we welcome new technologies, tools and workflows alike. Information is not hoarded; knowledge is shared, never privatized.',
    },
  },
  {
    zh: '勇敢',
    en: 'Courage',
    body: {
      zh: '勇敢是做「值得存在的内容」的前提。敢于尝试新的叙事形态，敢于面对观众的真实反馈，敢于在有风险的方向上下注。勇敢不是鲁莽——它是有准备的冒险。',
      en: 'Courage is the precondition for making content worth existing. Dare to try new narrative forms, to face the audience’s honest feedback, to bet on risky directions. Courage is not recklessness — it is adventure, prepared.',
    },
  },
  {
    zh: '真诚',
    en: 'Sincerity',
    body: {
      zh: '对内容真诚：不做虚伪的、悬浮的、讨好流量的作品，每一部作品都源于真实的关心与洞察。对人真诚：不表演友好，不玩办公室政治。真诚写出的故事，观众一定感知得到。',
      en: 'Sincere to the work: no hollow, floating, traffic-chasing content — every work grows from real care and insight. Sincere to people: no performative friendliness, no office politics. An audience can always feel a story written in earnest.',
    },
  },
  {
    zh: '美好',
    en: 'Beauty',
    body: {
      zh: '美好是我们的终极价值判断：能连接足够多的人、产生真实共鸣的创作。美好的另一面是克制——创造力是有边界的、收敛的、结构化地释放的。',
      en: 'Beauty is our final standard of judgment: work that reaches enough people and resonates for real. The other side of beauty is restraint — creativity released with boundary, convergence and structure.',
    },
  },
];

/** Builder 的四种人（品牌体系文档 §三） */
const TRAITS: { zh: string; en: string; body: Bi }[] = [
  {
    zh: '审美极好',
    en: 'Exceptional taste',
    body: {
      zh: '有敏锐的审美判断力，知道什么是好内容、什么是好的叙事方式。',
      en: 'A keen sense of what makes good content and good storytelling.',
    },
  },
  {
    zh: '有故事',
    en: 'Full of stories',
    body: {
      zh: '自身有丰富的人生经历与精神积累——没有生活素材，就写不出有灵魂的内容。',
      en: 'Rich lived experience and inner accumulation — without life’s material, there is no soul in the work.',
    },
  },
  {
    zh: '有表达能力',
    en: 'Expressive',
    body: {
      zh: '能把内心的故事转化为打动人心的作品，有成熟的、有经验的表达能力。',
      en: 'Able to turn the story within into work that moves people — mature, practiced expression.',
    },
  },
  {
    zh: '对世界有看法',
    en: 'A point of view',
    body: {
      zh: '不是只掌握技术的工匠，而是有观点、有态度、有立场的创作者。',
      en: 'Not artisans of technique alone, but creators with opinions, attitudes and stances.',
    },
  },
];

/** 小镜的双角色（品牌体系文档 §四）——2026-09-19 用户指示「核心引擎」整幕从关于页删除（后台 seed 不动） */

/** 圆点导航的幕名（aria 用；与幕渲染顺序一一对应） */
const SCREEN_LABELS = {
  zh: ['开篇', '名称由来', '使命与愿景', '品牌 Slogan', '价值观', '创作理念', '品牌架构', '联系我们'],
  en: ['Opening', 'The Name', 'Mission & Vision', 'Brand Slogan', 'Values', 'Philosophy', 'Brand Architecture', 'Contact'],
} as const;

/** 幕内元素 stagger 延迟（style 变量 --i） */
const fx = (i: number) => ({ '--i': i }) as CSSProperties;

/** Hero 大标题拆两行：中文按空格、英文按破折号断行（数据单行存储，仅展示层断行） */
function splitTitleLines(title: string): [string, string?] {
  const dash = title.indexOf('—');
  if (dash >= 0) return [title.slice(0, dash + 1).trim(), title.slice(dash + 1).trim()];
  const space = title.indexOf(' ');
  if (space >= 0) return [title.slice(0, space), title.slice(space + 1)];
  return [title];
}

export function AboutPage() {
  const { locale } = useLocale();
  const [envelope, setEnvelope] = useState<Envelope<PageContent> | null>(null);
  // 手风琴开合状态（key = 中文词；各项独立开合）
  const [openValues, setOpenValues] = useState<Record<string, boolean>>({});
  // 当前幕（圆点导航高亮）
  const [active, setActive] = useState(0);
  const viewportRef = useRef<HTMLDivElement>(null);
  const screenRefs = useRef<(HTMLElement | null)[]>([]);
  const setScreenRef = (i: number): Ref<HTMLElement> => (el) => {
    screenRefs.current[i] = el;
  };

  useEffect(() => {
    let cancelled = false;
    http
      .get<Envelope<PageContent>>(`/public/pages/about?locale=${locale}`)
      .then((res) => {
        if (!cancelled) setEnvelope(res);
      })
      .catch(() => {
        // 加载失败保持 envelope 为 null → 标题/文案走内置 fallback，页面不空白（AGENTS §7）
      });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  // 幕级 observer：进入视口 → 加 .ab-in（触发幕内 .fx stagger 渐显）+ 记录当前幕。
  // reduced-motion / 无 IntersectionObserver 时不启用渐显（内容恒可见）。
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    let reduced = false;
    try {
      reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      reduced = false;
    }
    const screens = screenRefs.current.filter(Boolean) as HTMLElement[];
    const activeIo = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const i = screens.indexOf(entry.target as HTMLElement);
          if (i >= 0) setActive(i);
        }
      },
      { root: viewport, threshold: 0.55 },
    );
    let revealIo: IntersectionObserver | null = null;
    if (!reduced && 'IntersectionObserver' in window) {
      viewport.classList.add('ab-reveal');
      revealIo = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add('ab-in');
              revealIo?.unobserve(entry.target);
            }
          }
        },
        { root: viewport, threshold: 0.25 },
      );
    }
    screens.forEach((s) => {
      activeIo.observe(s);
      revealIo?.observe(s);
    });
    return () => {
      activeIo.disconnect();
      revealIo?.disconnect();
      viewport.classList.remove('ab-reveal');
    };
  }, []);

  /** 跳到第 i 幕（下箭头 / 圆点导航共用；reduced-motion 用瞬时滚动） */
  const scrollToScreen = (i: number) => {
    const el = screenRefs.current[i];
    if (!el) return;
    let reduced = false;
    try {
      reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      reduced = false;
    }
    el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  };

  const zh = locale === 'zh-CN';
  const data = envelope?.data;
  const toggleValue = (key: string) => setOpenValues((s) => ({ ...s, [key]: !s[key] }));

  return (
    <main className='page ab-page' id='main'>
      <SiteHeader />
      <div className='ab-viewport' ref={viewportRef}>
        {/* 幕 0 · Hero：slogan 居中 + 下箭头 */}
        <section className='ab-screen ab-hero' ref={setScreenRef(0)} aria-label={zh ? '开篇' : 'Opening'}>
          <div className='ab-mesh' aria-hidden='true'>
            <i />
            <i />
            <i />
            <i />
          </div>
          {/* 颗粒层必须在 blur 容器之外（容器的 filter 会连子树一起模糊，颗粒会消失） */}
          <span
            className='ab-grain'
            aria-hidden='true'
            style={{ backgroundImage: `url(${grainImg})` } as CSSProperties}
          />
          <div className='ab-hero-inner'>
            <h1 className='ab-title fx' style={fx(0)}>
              {(() => {
                const [l1, l2] = splitTitleLines(
                  data?.title ??
                    (zh ? '时间不是线性的 故事却可以永恒' : 'Time is not linear — stories can be eternal.'),
                );
                return (
                  <>
                    <span className='ab-title-line'>{l1}</span>
                    {l2 && <span className='ab-title-line'>{l2}</span>}
                  </>
                );
              })()}
              {data && <DraftBadge text={data.title} />}
            </h1>
            {envelope && <LocaleFallbackNotice fallbackUsed={envelope.fallbackUsed} />}
          </div>
          <button
            type='button'
            className='ab-down'
            onClick={() => scrollToScreen(1)}
            aria-label={zh ? '向下翻页：名称由来' : 'Next screen: The Name'}
          >
            <svg viewBox='0 0 24 24' width='20' height='20' aria-hidden='true' fill='none' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round'>
              <path d='M12 4v16M5 13l7 7 7-7' />
            </svg>
          </button>
        </section>

        {/* 幕 1 · 01 名称由来 */}
        <section className='ab-screen' ref={setScreenRef(1)} aria-label={zh ? '名称由来' : 'The Name'}>
          <div className='ab-screen-inner'>
            <p className='ab-chapter fx' style={fx(0)}>
              {zh ? '01 · 名称由来' : '01 · The Name'}
            </p>
            <h2 className='fx' style={fx(1)}>
              {zh ? 'Chrono，时间之神' : 'Chrono, the god of time'}
            </h2>
            <blockquote className='ab-quote fx' style={fx(2)}>
              {zh
                ? '「如果时间会说话，那么它一定是在讲故事。」'
                : '“If time could speak, it would surely be telling stories.”'}
            </blockquote>
            <p className='ab-body fx' style={fx(3)}>
              {zh
                ? 'Chronuspeak 的名字源于古希腊神话——Chrono 是时间之神。时间是承载一切故事的容器，而我们的工作，就是将时间里沉淀的故事，转化为有生命力的内容。中文名「时述智能」由此而来——时间之述，以智能为器。'
                : 'The name Chronuspeak comes from Greek mythology — Chrono is the god of time. Time is the vessel that carries every story, and our work is to turn the stories settled in time into living content. The Chinese name 时述智能 reads: narrating time, with intelligence as the instrument.'}
            </p>
          </div>
        </section>

        {/* 幕 2 · 02 使命愿景（无标题；双栏隐约分界线，标签放大——2026-09-19 用户定稿） */}
        <section className='ab-screen' ref={setScreenRef(2)} aria-label={zh ? '使命与愿景' : 'Mission & Vision'}>
          <div className='ab-screen-inner'>
            <p className='ab-chapter fx' style={fx(0)}>
              {zh ? '02 · 使命与愿景' : '02 · Mission & Vision'}
            </p>
            <div className='ab-mv fx' style={fx(1)}>
              <div className='ab-mv-card'>
                <p className='ab-label'>{zh ? '使命 · Mission' : 'Mission · 使命'}</p>
                <p className='ab-mv-text'>
                  {zh ? '让优秀创作者被看见、被尊重、被需要。' : 'Let great creators be seen, respected and needed.'}
                </p>
              </div>
              <div className='ab-mv-card'>
                <p className='ab-label'>{zh ? '愿景 · Vision' : 'Vision · 愿景'}</p>
                <p className='ab-mv-text'>
                  {zh
                    ? '成为 AI 时代全球最优质的内容超级工厂。'
                    : 'To become the world’s finest content super factory of the AI era.'}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 幕 3 · Slogan 幕间（全屏标语） */}
        <section className='ab-screen ab-band-screen' ref={setScreenRef(3)} aria-label={zh ? '品牌 Slogan' : 'Brand Slogan'}>
          <div className='ab-screen-inner'>
            <p className='ab-band-zh fx' style={fx(0)}>
              时间共振，故事无限
            </p>
            <p className='ab-band-en fx' style={fx(1)}>
              TIME IS NOT LINEAR, STORIES ARE ETERNAL
            </p>
          </div>
        </section>

        {/* 幕 4 · 03 价值观：五项手风琴 */}
        <section className='ab-screen' ref={setScreenRef(4)} aria-label={zh ? '价值观' : 'Values'}>
          <div className='ab-screen-inner'>
            <p className='ab-chapter fx' style={fx(0)}>
              {zh ? '03 · 价值观' : '03 · Values'}
            </p>
            <h2 className='fx' style={fx(1)}>
              {zh ? '价值观' : 'Values'}
            </h2>
            <p className='ab-body fx' style={fx(2)}>
              {zh
                ? '五个词，共同定义了我们是怎样的一群人、以及我们如何做内容。'
                : 'Five words that define who we are and how we make content.'}
            </p>
            <div className='ab-values fx' style={fx(3)}>
              {VALUES.map((v, i) => {
                const open = !!openValues[v.zh];
                const panelId = `ab-value-panel-${i}`;
                const buttonId = `ab-value-button-${i}`;
                return (
                  <div key={v.zh} className='ab-value' data-open={open}>
                    <button
                      type='button'
                      className='ab-value-button'
                      id={buttonId}
                      aria-expanded={open}
                      aria-controls={panelId}
                      onClick={() => toggleValue(v.zh)}
                    >
                      <span className='ab-value-index' aria-hidden='true'>
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className='ab-value-word'>
                        <strong>{zh ? v.zh : v.en}</strong>
                        <small>{zh ? v.en : v.zh}</small>
                      </span>
                      <span className='ab-value-plus' aria-hidden='true'>
                        +
                      </span>
                    </button>
                    <div
                      className='ab-value-panel'
                      id={panelId}
                      role='region'
                      aria-labelledby={buttonId}
                      aria-hidden={!open}
                    >
                      <div className='ab-value-panel-inner'>
                        <p>{zh ? v.body.zh : v.body.en}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className='ab-values-close fx' style={fx(4)}>
              {zh
                ? '这五个词共同构成时述智能的灵魂底色——它们不只是墙上的文字，而是选人、选故事、选方向时的真实标准。'
                : 'Together, these five words form the soul of Chronuspeak — not words on a wall, but the real standards by which we choose people, stories and directions.'}
            </p>
          </div>
        </section>

        {/* 幕 5 · 04 创作理念：Builder */}
        <section className='ab-screen' ref={setScreenRef(5)} aria-label={zh ? '创作理念' : 'Philosophy'}>
          <div className='ab-screen-inner'>
            <p className='ab-chapter fx' style={fx(0)}>
              {zh ? '04 · 创作理念' : '04 · Philosophy'}
            </p>
            <h2 className='fx' style={fx(1)}>
              {zh ? '我们不是艺术家，我们是 Builder' : 'We are not artists. We are Builders.'}
            </h2>
            <p className='ab-body fx' style={fx(2)}>
              {zh
                ? '艺术家可以进行纯粹的个人表达；而 Builder 的创作必须作用于真实的观众、商业项目或商业作品。一个找不到观众、找不到可以影响的人群的作品，在我们的定义里是不美好的。'
                : 'An artist may pursue pure personal expression. A Builder’s work, however, must reach real audiences, commercial projects or commercial works. A work that finds no one to touch is, by our definition, not yet beautiful.'}
            </p>
            <p className='ab-label fx' style={fx(3)}>
              {zh ? '在这里工作的人' : 'The people who work here'}
            </p>
            <div className='ab-traits fx' style={fx(4)}>
              {TRAITS.map((t) => (
                <div key={t.zh} className='ab-trait'>
                  <strong>{zh ? t.zh : t.en}</strong>
                  <p>{zh ? t.body.zh : t.body.en}</p>
                </div>
              ))}
            </div>
            <blockquote className='ab-philosophy fx' style={fx(5)}>
              {zh
                ? '有边界的、收敛的、克制的创造力。就像好的叙事需要节奏和结构，好的创作也需要边界和纪律——我们追求的不是喧闹的刺激，而是安静的震撼。'
                : 'Creativity that is bounded, convergent and restrained. As good narrative needs rhythm and structure, good creation needs boundaries and discipline — what we pursue is not loud stimulation, but quiet awe.'}
            </blockquote>
          </div>
        </section>

        {/* 05 核心引擎（小镜）幕已按用户指示删除（2026-09-19）——内容仍见品牌体系文档与 seed */}

        {/* 幕 6 · 06 品牌架构（纯展示，决策 7） */}
        <section className='ab-screen' ref={setScreenRef(6)} aria-label={zh ? '品牌架构' : 'Brand Architecture'}>
          <div className='ab-screen-inner'>
            <p className='ab-chapter fx' style={fx(0)}>
              {zh ? '06 · 品牌架构' : '06 · Brand Architecture'}
            </p>
            <h2 className='fx' style={fx(1)}>
              {zh ? '一棵树，三层枝' : 'One tree, three branches'}
            </h2>
            <p className='ab-body fx' style={fx(2)}>
              {zh
                ? 'Chronuspeak Tech 是公司，Innaframe 是工具品牌，Originals 是未来内容的署名标签。'
                : 'Chronuspeak Tech is the company, Innaframe is the tool brand, and Originals is the label signed on original works to come.'}
            </p>
            <div className='ab-tree fx' style={fx(3)}>
              <div className='ab-tree-root'>
                <strong>Chronuspeak Tech</strong>
                <small>
                  {zh
                    ? '公司品牌 · AI 时代的内容超级工厂'
                    : 'Corporate brand · The content super factory of the AI era'}
                </small>
              </div>
              <ul className='ab-tree-children'>
                {ARCH_TREE.map((node) => (
                  <li key={node.name}>
                    <strong>{node.name}</strong>
                    <small>{zh ? node.zh : node.en}</small>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* 幕 7 · 收尾：CTA */}
        <section className='ab-screen ab-closing' ref={setScreenRef(7)} aria-label={zh ? '联系我们' : 'Contact'}>
          <div className='ab-screen-inner'>
            <p className='ab-cta-line fx' style={fx(0)}>
              {zh ? '时间会说话。想和这座小镇聊聊你的故事吗？' : 'Time speaks. Want to tell the town your story?'}
            </p>
            <p className='fx' style={fx(1)}>
              <Link className='button primary' to={`/${locale}/contact`}>
                {zh ? '联系我们' : 'Contact us'}
              </Link>
            </p>
            <p className='ab-sign fx' style={fx(2)}>
              时述智能 · Chronuspeak Tech
            </p>
          </div>
        </section>
      </div>

      {/* 右侧圆点导航（桌面；跳幕，当前幕高亮） */}
      <nav className='ab-dots' aria-label={zh ? '页面导航' : 'Screen navigation'}>
        {(zh ? SCREEN_LABELS.zh : SCREEN_LABELS.en).map((label, i) => (
          <button
            key={label}
            type='button'
            onClick={() => scrollToScreen(i)}
            aria-label={`${zh ? '跳至' : 'Go to'} ${label}`}
            aria-current={active === i ? 'true' : undefined}
          />
        ))}
      </nav>
    </main>
  );
}
